import type { ScopeRef } from "@enduring-systems/entitlements";
import { scopeKey } from "@enduring-systems/entitlements";
import { useEffect, useRef, useState } from "react";

import {
  type PendingRequestView,
  readRequestQueue,
  REQUEST_QUEUE_LIMIT,
} from "../entitlements-api";
import { sortOldestFirst } from "./request-age";

export interface UseRequestQueueReturn {
  /**
   * The platform-wide pending queue, longest-waiting first. Null until the
   * read has answered; an empty array is a real "nothing pending".
   */
  readonly requests: readonly PendingRequestView[] | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  /**
   * The read filled its limit, so this is a page of the queue rather than the
   * queue. Every count taken from it is a FLOOR, and a surface has to say so
   * rather than presenting it as the total.
   */
  readonly capped: boolean;
  /**
   * The age at which the ENR-1937 queue-age alarm fires in the environment
   * that served this read, or null when that deployment injected none. Never
   * defaulted: see `isAged`.
   */
  readonly maxAgeSeconds: number | null;
}

interface QueueState {
  readonly key: number;
  readonly requests: readonly PendingRequestView[] | null;
  readonly capped: boolean;
  readonly maxAgeSeconds: number | null;
  readonly error: string | null;
  readonly isLoading: boolean;
}

/**
 * The pending request queue, platform-wide, read once per `reloadKey`.
 *
 * ## Why the read is not per scope
 *
 * The store has no per-scope form of it: the pending index is keyed for the CS
 * queue. Every consumer therefore filters this one list, which is also why
 * there is exactly ONE of these reads on the page. Two would be two audited
 * cross-tenant disclosures for one fact, and two sources for one piece of
 * content, which is how a workspace tab and a landing-page count come to
 * disagree about the same queue.
 *
 * Reading independently of the scope is also what removes the attribution race
 * the per-scope version had to guard: nothing in this response describes one
 * workspace, so there is no way for an answer to be painted under the wrong
 * one. The scope filter is a pure function over the result (`scopeRequests`).
 *
 * ## Why a reload key of its own
 *
 * `reloadKey` here is bumped by a DECISION, not by the page's grant and bundle
 * writes. Those cannot create, approve or deny a request, so re-reading on one
 * would file an extra audited pull of every tenant's pending queue for a
 * change that could not have altered it.
 */
export function useRequestQueue(reloadKey: number): UseRequestQueueReturn {
  /* Mirrored during RENDER, not in an effect: an effect runs after paint, so a
     ref set there still holds the previous key for one commit, and a response
     landing in that commit would be accepted against a read already replaced. */
  const currentKey = useRef(reloadKey);
  currentKey.current = reloadKey;

  const [state, setState] = useState<QueueState>({
    key: reloadKey,
    requests: null,
    capped: false,
    maxAgeSeconds: null,
    error: null,
    isLoading: true,
  });

  useEffect(() => {
    setState({
      key: reloadKey,
      requests: null,
      capped: false,
      maxAgeSeconds: null,
      error: null,
      isLoading: true,
    });

    readRequestQueue(REQUEST_QUEUE_LIMIT)
      .then((queue) => {
        if (currentKey.current !== reloadKey) return;
        setState({
          key: reloadKey,
          requests: sortOldestFirst(queue.requests),
          capped: queue.requests.length >= REQUEST_QUEUE_LIMIT,
          maxAgeSeconds: queue.maxAgeSeconds,
          error: null,
          isLoading: false,
        });
      })
      .catch((cause: unknown) => {
        if (currentKey.current !== reloadKey) return;
        setState({
          key: reloadKey,
          requests: null,
          capped: false,
          maxAgeSeconds: null,
          error: cause instanceof Error ? cause.message : "Failed to read the request queue",
          isLoading: false,
        });
      });
  }, [reloadKey]);

  if (state.key === reloadKey) {
    return {
      requests: state.requests,
      isLoading: state.isLoading,
      error: state.error,
      capped: state.capped,
      maxAgeSeconds: state.maxAgeSeconds,
    };
  }
  /* The key moved and the effect has not run yet: report the read as pending
     rather than handing back the answer to the read it replaced. */
  return { requests: null, isLoading: true, error: null, capped: false, maxAgeSeconds: null };
}

/**
 * The slice of the queue belonging to one scope.
 *
 * Compared by `scopeKey`, never by id: a facility and an enterprise can share
 * an id, and crediting one's requests to the other would put another tenant's
 * ask on this page.
 *
 * Null when the queue has not answered, or when no scope is selected, so a
 * caller cannot mistake "no answer yet" for "nothing pending here".
 */
export function scopeRequests(
  requests: readonly PendingRequestView[] | null,
  scope: ScopeRef | null
): readonly PendingRequestView[] | null {
  if (requests === null || scope === null) return null;
  const wanted = scopeKey(scope);
  return requests.filter((request) => scopeKey(request.scope) === wanted);
}
